import {
	PRECIP_DAY_BYTES,
	PRECIP_MANIFEST_VERSION,
	WeatherUnavailableError,
	isPrecipLattice,
	type PrecipManifest,
	type WeatherPoint,
	type WeatherUnavailableReason,
} from "@domain/Weather";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { apiClient } from "../client";
import { StandardAxiosConfig } from "../common";
import { env } from "../../config/env";

export interface WeatherRange {
	/** The 0.1° lattice point the series was read from (within 0.05° of the request). */
	latitude: number;
	longitude: number;
	/** Every hour of the requested UTC days, in order. */
	points: WeatherPoint[];
	/** ISO yyyy-mm-dd: the first day of precipitation history. Every hour before it is null. */
	archivedFrom: string;
	/** The end of the latest observed hour, ms since epoch (UTC): every point from it on is
	 *  forecast, and every earlier one with data was observed. Null when nothing is observed yet,
	 *  in which case no point is forecast. */
	observedUntil: number | null;
}

/** `/weather/range` as the API sends it: `observedUntil` is an ISO-8601 UTC datetime (absent from
 *  an API older than the observed/forecast seam). */
type WeatherRangeResponse = Omit<WeatherRange, "observedUntil"> & { observedUntil?: string | null };

/** `/weather/precip/manifest` as the API sends it (API `types/precip.py` `PrecipManifest`): datetimes are
 *  ISO-8601 UTC, and `grid` echoes the lattice the day files are laid out on. */
interface PrecipManifestResponse {
	version: number;
	buildId: string;
	builtAt: string;
	builtBy: string;
	latestObservedHour: string | null;
	hourlyCycle: string | null;
	extendedCycle: string | null;
	grid: { west: number; east: number; south: number; north: number; step: number; cols: number; rows: number };
	days: { date: string; id: string; final: boolean; cycles: string[] }[];
}

/** Precipitation from the customer-portal API, which reads the NOAA-derived precipitation store
 *  (observed hours, then forecast): a point's hourly range (`API/src/routers/weather.py`), and the
 *  national build's manifest and day files (`API/src/routers/weather_precip.py`). */
export class WeatherAPI {
	private baseUrl: string;

	constructor() {
		this.baseUrl = `${env.rogoApiUrl}/weather`;
	}

	/** Fetch hourly precipitation for a lat/lon over a UTC date range (inclusive on both ends):
	 *  observed hours, forecast hours after the latest observed one, null where there is no data.
	 *  Rejects as {@link quietGet} does. */
	public async range(lat: number, lon: number, start: Date, end: Date): Promise<WeatherRange> {
		const response = await this.quietGet<WeatherRangeResponse>("/range", {
			...StandardAxiosConfig(),
			params: {
				lat,
				lon,
				start: toISODate(start),
				end: toISODate(end),
			},
		});
		const { observedUntil, ...range } = response.data;
		return { ...range, observedUntil: parseUtcInstant(observedUntil) };
	}

	/** Fetch the manifest of the latest national precipitation build: which day file holds each
	 *  day of its window. `have` is the build the caller already holds, sent as a parameter (never
	 *  part of the path: the e2e request budget keys on the bare pathname); the API answers 204 for
	 *  it, and this resolves null. Rejects as {@link quietGet} does, and `unexpected` for a manifest
	 *  of another version or lattice, which this client can't read. */
	public async precipManifest(have?: string): Promise<PrecipManifest | null> {
		const response = await this.quietGet<PrecipManifestResponse>("/precip/manifest", {
			...StandardAxiosConfig(30),
			...(have === undefined ? {} : { params: { have } }),
			// Branch on the status, never on `data`: axios gives "" for a 204.
			validateStatus: (status) => status === 200 || status === 204,
		});
		if (response.status === 204) return null;
		return toPrecipManifest(response.data);
	}

	/** Fetch one precipitation day file by its id (a content hash, so the response never changes
	 *  and the browser caches it). The API sends it gzip-encoded and the browser undoes that, so the
	 *  body is the raw file. Rejects as {@link quietGet} does, and `unexpected` for a body that
	 *  isn't a whole day file. */
	public async precipDay(id: string): Promise<ArrayBuffer> {
		const { data } = await this.quietGet<unknown>("/precip/day", {
			...StandardAxiosConfig(60),
			params: { id },
			responseType: "arraybuffer",
		});
		if (!(data instanceof ArrayBuffer) || data.byteLength !== PRECIP_DAY_BYTES) {
			throw new WeatherUnavailableError("unexpected");
		}
		return data;
	}

	/** GET `path` under `/weather`, rejecting with a {@link WeatherUnavailableError} naming why.
	 *  Every weather fetch opts out of the app-wide error toast and Sentry report: each is made in
	 *  the background for an optional surface that explains its own failures, and the backend
	 *  reports its own faults. */
	private async quietGet<T>(path: string, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		try {
			return await apiClient.get<T>(`${this.baseUrl}${path}`, {
				...config,
				suppressErrorToast: true,
				suppressErrorReport: true,
			});
		} catch (err: unknown) {
			throw unavailableError(err);
		}
	}

	/** Build the URL for a NEXRAD radar frame PNG. The backend snaps `timestampMs` to the
	 *  5-min frame grid and proxies + S3-caches Iowa State Mesonet's CONUS composite.
	 *  Returns just the URL string (no fetch — MapLibre's image source fetches the bytes
	 *  itself, so we get free browser caching off the immutable Cache-Control header). */
	public radarFrameUrl(timestampMs: number): string {
		return `${this.baseUrl}/radar?ts=${timestampMs}`;
	}
}

/** The weather endpoints' failure statuses: 503 is the API saying no precipitation has been built
 *  yet (or its index is unreadable); 504 and 502 are the gateway in front of the API timing out or
 *  getting no good answer. A request that ran out its own timeout (axios `ECONNABORTED`, or
 *  `ETIMEDOUT`) timed out too. Anything else — a 400, a 404, a 500, or no response at all — is
 *  unexpected. */
function unavailableReason(status: number | undefined, code?: string): WeatherUnavailableReason {
	if (code === "ECONNABORTED" || code === "ETIMEDOUT") return "timed-out";
	switch (status) {
		case 503:
			return "unavailable";
		case 504:
			return "timed-out";
		case 502:
			return "gateway-error";
		default:
			return "unexpected";
	}
}

/** A failed request as the {@link WeatherUnavailableError} naming why, keeping it as the cause. */
function unavailableError(err: unknown): WeatherUnavailableError {
	const { response, code } = err as { response?: { status?: number }; code?: string };
	return new WeatherUnavailableError(unavailableReason(response?.status, code), { cause: err });
}

/** The domain manifest, or `unexpected` for one this client can't read: another version, another
 *  lattice (its day files would decode into the wrong places), or not a manifest at all. */
function toPrecipManifest(data: PrecipManifestResponse): PrecipManifest {
	if (
		typeof data !== "object" ||
		data === null ||
		data.version !== PRECIP_MANIFEST_VERSION ||
		typeof data.grid !== "object" ||
		data.grid === null ||
		!isPrecipLattice(data.grid) ||
		!Array.isArray(data.days)
	) {
		throw new WeatherUnavailableError("unexpected");
	}
	return {
		buildId: data.buildId,
		builtBy: data.builtBy,
		latestObservedHourMs: parseUtcInstant(data.latestObservedHour),
		extendedCycleMs: parseUtcInstant(data.extendedCycle),
		days: data.days.map(({ date, id }) => ({ date, id })),
	};
}

/** An ISO-8601 instant as ms since epoch; one with no zone designator is read as UTC (a bare
 *  date-time would otherwise parse as local time). Null when absent or unreadable. */
function parseUtcInstant(iso: string | null | undefined): number | null {
	if (!iso) return null;
	const zoned = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
	const ms = Date.parse(zoned);
	return Number.isNaN(ms) ? null : ms;
}

function toISODate(d: Date): string {
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}
